<script setup lang="ts">
import { toRefs, ref, Ref, inject, computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import useVuelidate from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { useToast } from 'primevue/usetoast';
import OrganizationContractRemoveDisciplineModal from './OrganizationContractRemoveDisciplineModal.vue';

import { useOrganizationContractsStore } from '@/store';
import { CONTRACT_ENUMS } from '@/enum';
import { DisciplineInterface } from '@/types';

interface Props {
  scope: string;
}

const props = defineProps<Props>();
const contractStore = useOrganizationContractsStore();
const toast = useToast();
const canEdit = contractStore.canEditOrCreateContracts;
const { availableDisciplinesData, editType } = storeToRefs(contractStore);
const { scope } = toRefs(props);
const loading = ref(true);
const selectedMainForm = inject<Ref<CONTRACT_ENUMS.FORMS>>('selectedForm', ref(CONTRACT_ENUMS.FORMS.FLAT_FEE));
const removeDisciplineModalOpen = ref(false);
const disciplineToRemove = ref<DisciplineInterface>();
const disciplineContractTypes = [
  CONTRACT_ENUMS.CONTRACT_BILLING_TYPE.FLAT_FEE,
  CONTRACT_ENUMS.CONTRACT_BILLING_TYPE.HOURLY,
  CONTRACT_ENUMS.CONTRACT_BILLING_TYPE.PER_STUDENT,
];

onMounted(async () => {
  if (editType.value === CONTRACT_ENUMS.CONTRACT_EDIT_TYPES.EDIT) {
    await checkDisciplineEventCounts();
  }

  loading.value = false;
});

const checkDisciplineEventCounts = async () => {
  contractDisciplines.value
    .filter((discipline) => discipline.active)
    .forEach(async (discipline) => {
      const response = await contractStore.getDisciplineEventCount(discipline.id);
      discipline.event_count = response.events_count;
    });
};

const disciplinesActive = () => {
  return (
    (contractDisciplines.value as DisciplineInterface[]).filter((discipline) => {
      return discipline.active;
    }).length > 0
  );
};

const rules = computed(() => ({
  disciplines: {
    disciplinesActive: helpers.withMessage('At least one discipline must be active', disciplinesActive),
  },
}));

const contractDisciplines = computed<DisciplineInterface[]>(() =>
  sortDiscipline(availableDisciplinesData.value[disciplineContractTypes[selectedMainForm.value]]),
);

const sortDiscipline = (arr: DisciplineInterface[]) => {
  const sortedList = arr.sort((a, b) => {
    const stringA = a.discipline?.name;
    const stringB = b.discipline?.name;
    if (stringA && stringB) return stringA.localeCompare(stringB);
    else return 1;
  });

  return sortedList;
};

const removedFromContractDate = (date: string) => {
  if (date) {
    const documentByDate = new Date(date);
    const followingMonth = ((documentByDate.getMonth() + 1) % 12) + 1;
    const yearValue = followingMonth !== 1 ? documentByDate.getFullYear() : documentByDate.getFullYear() + 1;

    return `${followingMonth}/01/${yearValue}`;
  }
  return '';
};

const documentByDate = (date: string) => {
  if (date) {
    const documentByDate = new Date(date);
    const month = (documentByDate.getMonth() + 1) % 12;
    const day = documentByDate.getDate();
    const year = documentByDate.getFullYear();

    return `${month}/${day}/${year}`;
  }
  return '';
};

const disciplineToggled = (discipline: DisciplineInterface) => {
  const disablingDiscipline =
    discipline.active && discipline.contract_id && editType.value === CONTRACT_ENUMS.CONTRACT_EDIT_TYPES.EDIT;
  if (disablingDiscipline) {
    disciplineToRemove.value = discipline;
    if (!contractStore.selectedContractStarted || discipline.event_count === 0) {
      onRemove({ schedule_by_date: null, document_by_date: null }, false);
      return;
    }

    discipline.active = true;
    toggleConfirmRemoveDisciplineModal();
  }
};

const onRemove = async (endDates: Record<string, string | null>, closeModal = true) => {
  if (closeModal) toggleConfirmRemoveDisciplineModal();
  await contractStore.deleteDiscipline(disciplineToRemove.value?.id as number, endDates);
  (disciplineToRemove.value as DisciplineInterface).document_by_date = endDates.document_by_date;
  (disciplineToRemove.value as DisciplineInterface).schedule_by_date = endDates.schedule_by_date;
  if (closeModal) (disciplineToRemove.value as DisciplineInterface).active = true;
  disciplineToRemove.value = undefined;
  doToast('Removed', 'Discipline will be removed from the contract after changes have been saved');
};

const onRemoveDisciplineCancel = () => {
  const disciplineToRemoveIndex = contractDisciplines.value.findIndex(
    (discipline) => discipline.discipline_id === disciplineToRemove.value?.discipline_id,
  );
  contractDisciplines.value[disciplineToRemoveIndex].active = true;
  disciplineToRemove.value = undefined;
  toggleConfirmRemoveDisciplineModal();
};

const toggleConfirmRemoveDisciplineModal = () => {
  removeDisciplineModalOpen.value = !removeDisciplineModalOpen.value;
};

const undoDisciplineRemoval = async (discipline: DisciplineInterface) => {
  await contractStore.restoreDiscipline(discipline.id);
  discipline.document_by_date = null;
  discipline.schedule_by_date = null;
  contractStore.doToast('Discipline will no longer be removed from this contract after save', 'Success', false);
};

const doToast = (summary: string, detail: string) => {
  toast.add({
    severity: 'success',
    summary,
    detail,
    life: 3000,
  });
};

const v$ = useVuelidate(rules, { disciplines: contractDisciplines }, { $scope: scope.value });
</script>

<template>
  <div class="grid mx-0 p-2 pt-6">
    <h3 class="font-bold pl-5">Disciplines</h3>
    <div v-for="error of v$.disciplines.$errors" :key="error.$uid" class="input-errors p-error mx-3">
      <div class="p-error inline-flex align-items-center mt-0">
        <Icon name="info" class="discipline-error-icon mr-1 text-alert-red" />
        {{ error.$message }}
      </div>
    </div>
    <div class="mt-3 w-full" :class="{ 'p-invalid': v$.disciplines.$invalid && v$.disciplines.$dirty }">
      <DataTable
        :value="contractDisciplines"
        class="p-datatable-contracts"
        table-style="table-layout: fixed;"
        :class="{ 'table-loading': loading }"
        data-key="discipline_id"
        loading-icon="logo-spinner"
        :loading="loading"
        responsive-layout="scroll"
      >
        <template #empty>No disciplines found</template>
        <template v-if="contractDisciplines.length">
          <Column field="id" header-style="width: 8%" body-style="width: 8%; vertical-align: center;">
            <template #body="{ data }">
              <InputSwitch
                v-model="data.active"
                class="switch"
                :class="{ 'p-invalid': v$.disciplines.$invalid && v$.disciplines.$dirty }"
                :disabled="data.document_by_date || !canEdit"
                @click="() => disciplineToggled(data)"
                @change="v$.disciplines.touch"
              />
            </template>
          </Column>

          <Column field="name" header="Discipline" style="width: 20%">
            <template #body="{ data }"><span v-text="data.discipline.name" /></template>
          </Column>
          <Column
            v-if="selectedMainForm === CONTRACT_ENUMS.FORMS.HOURLY"
            field="rate"
            header="Billing Rate"
            style="width: 12%"
          >
            <template #body="{ data }">
              <InputNumber
                v-model="data.contract_type_data.rate"
                mode="currency"
                currency="USD"
                locale="en-US"
                allow-empty
                input-class="w-6rem"
                :disabled="!data.active || !canEdit"
                @focus="(e: Event) => e?.target?.select()"
              />
            </template>
          </Column>
          <Column body-style="width: 30%">
            <template #body="{ data }">
              <div
                v-if="data.document_by_date"
                class="flex flex-column flex-wrap align-content-center justify-content-end ml-auto"
              >
                <span class="pb-2 flex justify-content-end">
                  Will no longer appear on this contract after
                  <b class="pl-1">{{ removedFromContractDate(data.document_by_date) }}</b>
                </span>
                <span class="flex justify-content-end">
                  Cannot create or document events after
                  <b class="pl-1">{{ documentByDate(data.document_by_date) }}</b>
                </span>
                <button
                  class="p-link flex ml-auto pt-2 align-items-center"
                  :disabled="!canEdit"
                  @click="() => undoDisciplineRemoval(data)"
                >
                  <Icon name="undo" size="text-xl" class="text-primary mt-1" />
                  <span class="p-button-label text-primary ml-1"><b>Undo Remove</b></span>
                </button>
              </div>
            </template>
          </Column>

          <template v-if="selectedMainForm !== CONTRACT_ENUMS.FORMS.PER_STUDENT">
            <div v-if="selectedMainForm === CONTRACT_ENUMS.FORMS.FLAT_FEE">
              <Column field="rate" header="Annual Rate" header-class="w-6rem" body-class="w-1">
                <template #body="{ data }">
                  <InputNumber
                    v-model="data.contract_type_data.annual_rate"
                    mode="currency"
                    currency="USD"
                    locale="en-US"
                    allow-empty
                    input-class="w-6rem text-sm"
                    :disabled="!data.active || !canEdit"
                    @focus="(e: Event) => e?.target?.select()"
                  />
                </template>
              </Column>
              <Column field="hours" header="Hours Contracted" header-class="w-6rem" body-class="w-1">
                <template #body="{ data }">
                  <InputNumber
                    v-model="data.contract_type_data.hours_contracted"
                    mode="decimal"
                    :max-fraction-digits="2"
                    :min-fraction-digits="0"
                    :min="0"
                    locale="en-US"
                    input-class="w-6rem text-sm"
                    :disabled="!data.active || !canEdit"
                    @focus="(e: Event) => e?.target?.select()"
                  />
                </template>
              </Column>
              <Column field="fte" header="Evaluations Only" header-class="w-8rem" body-class="text-center w-1">
                <template #body="{ data }">
                  <InputSwitch
                    v-if="data.active"
                    v-model="data.contract_type_data.evaluations"
                    :true-value="1"
                    :false-value="0"
                    class="switch"
                    :disabled="!data.active || !canEdit"
                  />
                  <span v-else>-</span>
                </template>
              </Column>
            </div>
          </template>
          <template v-if="selectedMainForm === CONTRACT_ENUMS.FORMS.PER_STUDENT">
            <Column field="per_student_rate" header="PSPY Fee">
              <template #body="{ data }">
                <InputNumber
                  v-model="data.contract_type_data.per_student_rate"
                  mode="currency"
                  currency="USD"
                  locale="en-US"
                  :min="0"
                  input-class="w-6rem"
                  :disabled="!data.active || !canEdit"
                  @focus="(e: Event) => e?.target?.select()"
                />
              </template>
            </Column>
            <Column field="per_month_rate_fee" header="PSPM Fee">
              <template #body="{ data }">
                <InputNumber
                  v-model="data.contract_type_data.per_month_rate"
                  mode="currency"
                  currency="USD"
                  locale="en-US"
                  input-class="w-6rem"
                  :disabled="!data.active || !canEdit"
                  @focus="(e: Event) => e?.target?.select()"
                />
              </template>
            </Column>

            <Column field="hours_per_week" header="Hours Per Week">
              <template #body="{ data }">
                <InputNumber
                  v-model="data.contract_type_data.hours_per_week"
                  mode="decimal"
                  :max-fraction-digits="2"
                  :min-fraction-digits="0"
                  :min="0"
                  locale="en-US"
                  input-class="w-6rem"
                  :disabled="!data.active || !canEdit"
                  @focus="(e: Event) => e?.target?.select()"
                />
              </template>
            </Column>
            <Column field="minimum" header="Seats">
              <template #body="{ data }">
                <InputNumber
                  v-model="data.contract_type_data.minimum"
                  :min="0"
                  input-class="w-6rem"
                  :disabled="!data.active || !canEdit"
                  @focus="(e: Event) => e?.target?.select()"
                />
              </template>
            </Column>

            <Column field="weeks_in_year" header="Weeks in a Year">
              <template #body="{ data }">
                <InputNumber
                  v-model="data.contract_type_data.weeks_in_year"
                  :min="0"
                  input-class="w-6rem"
                  :disabled="!data.active || !canEdit"
                  @focus="(e: Event) => e?.target?.select()"
                />
              </template>
            </Column>
          </template>
        </template>
      </DataTable>
    </div>
    <OrganizationContractRemoveDisciplineModal
      v-if="removeDisciplineModalOpen"
      :discipline="disciplineToRemove as DisciplineInterface"
      @remove="onRemove"
      @remove-cancel="onRemoveDisciplineCancel"
    />
  </div>
</template>

<style lang="scss" scoped>
.discipline-error-icon {
  font-size: 0.875rem !important;
}
:deep(.p-datatable-contracts) {
  .p-column-title {
    font-size: 14px;
  }
}
</style>
